import {
  Box,
  Link,
  List,
  ListItem,
  ListItemText,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useTheme,
} from '@mui/material';
import GlobalStyles from '@mui/material/GlobalStyles';
import { memo, useMemo } from 'react';
import { Components, default as ReactMarkdown } from 'react-markdown';
import { default as SyntaxHighlighter } from 'react-syntax-highlighter';
import {
  tomorrowNight as darkHighlightStyle,
  tomorrow as lightHighlightStyle,
} from 'react-syntax-highlighter/dist/cjs/styles/hljs';
import remarkBreaks from 'remark-breaks';
import remarkDirective from 'remark-directive';
import remarkDirectiveRehype from 'remark-directive-rehype';
import remarkGfm from 'remark-gfm';

export interface MUIMarkdownProps {
  markdown: string;
  components?: Components;
}

export const MUIMarkdown = memo<MUIMarkdownProps>(
  ({ markdown, components: componentsProp }: MUIMarkdownProps) => {
    const theme = useTheme();

    const components: Components = useMemo(() => {
      return {
        // *********
        // * Links *
        // *********
        a: ({ href, title, children }) => (
          <Link href={href} underline={'always'} title={title} target="_blank">
            {children}
          </Link>
        ),

        // ********
        // * Text *
        // ********
        p: ({ children }) => <Typography sx={{ mt: 2 }}>{children}</Typography>,
        del: ({ children }) => (
          <Typography
            component="del"
            sx={{ mt: 1, textDecoration: 'line-through' }}
          >
            {children}
          </Typography>
        ),
        em: ({ children }) => (
          <Typography component="em" sx={{ mt: 1, fontStyle: 'italic' }}>
            {children}
          </Typography>
        ),
        strong: ({ children }) => (
          <Typography component="strong" sx={{ mt: 1, fontWeight: 'bold' }}>
            {children}
          </Typography>
        ),
        b: ({ children }) => (
          <Typography component="b" sx={{ mt: 1, fontWeight: 'bold' }}>
            {children}
          </Typography>
        ),
        h1: ({ children }) => (
          <Typography gutterBottom sx={{ mt: 2 }} variant={'h6'}>
            {children}
          </Typography>
        ),
        h2: ({ children }) => (
          <Typography gutterBottom sx={{ mt: 2 }} variant={'h6'}>
            {children}
          </Typography>
        ),
        h3: ({ children }) => (
          <Typography gutterBottom sx={{ mt: 2 }} variant={'h6'}>
            {children}
          </Typography>
        ),
        h4: ({ children }) => (
          <Typography gutterBottom sx={{ mt: 2 }} variant={'h6'}>
            {children}
          </Typography>
        ),
        h5: ({ children }) => (
          <Typography gutterBottom sx={{ mt: 2 }} variant={'h6'}>
            {children}
          </Typography>
        ),
        h6: ({ children }) => (
          <Typography gutterBottom sx={{ mt: 2 }} variant={'h6'}>
            {children}
          </Typography>
        ),
        blockquote: ({ children }) => (
          <Typography
            component="blockquote"
            gutterBottom
            sx={{
              borderLeft: (theme) => `4px solid ${theme.palette.divider}`,
              pl: 2,
              mt: 2,
              mb: 0,
              color: 'text.secondary',
              fontSize: (theme) => theme.typography.body2.fontSize,
              '& *:not(svg)': {
                fontSize: 'inherit !important',
                color: 'inherit !important',
              },
            }}
          >
            {children}
          </Typography>
        ),

        // **********
        // * Tables *
        // **********
        table: ({ children }) => (
          <TableContainer component={Paper} sx={{ mt: 2 }}>
            <Table size="small">{children}</Table>
          </TableContainer>
        ),
        tbody: ({ children }) => <TableBody>{children}</TableBody>,
        th: ({ children }) => (
          <TableHead
            component="th"
            sx={{
              display: 'table-cell',
              verticalAlign: 'inherit',
              borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
              textAlign: 'left',
              padding: '16px',
            }}
          >
            <Typography component="span" variant="body2" fontWeight={500}>
              {children}
            </Typography>
          </TableHead>
        ),
        tr: ({ children }) => <TableRow>{children}</TableRow>,
        td: ({ children }) => (
          <TableCell>
            <Typography>{children}</Typography>
          </TableCell>
        ),

        // *********
        // * Lists *
        // *********
        ol: ({ children, ...props }) => (
          <List
            sx={{
              listStyleType: 'decimal',
              pt: 0,
              pl: 2,
              pb: 0,
              mt: 2,
              '& .MuiListItem-root': {
                display: 'list-item',
              },
            }}
            component="ol"
            start={props.start}
          >
            {children}
          </List>
        ),
        ul: ({ children }) => (
          <List
            sx={{
              listStyleType: 'disc',
              pt: 0,
              pl: 2,
              pb: 0,
              mt: 2,
              '& .MuiListItem-root': {
                display: 'list-item',
              },
            }}
          >
            {children}
          </List>
        ),
        li: ({ children }) => (
          <ListItem sx={{ m: 0, p: 0, mt: 0.5 }} disableGutters>
            <ListItemText sx={{ pl: 0.25 }}>{children}</ListItemText>
          </ListItem>
        ),

        pre: ({ children }) => {
          return (
            <Paper elevation={1} sx={{ p: 2, borderRadius: 1.5, mt: 2 }}>
              {children}
            </Paper>
          );
        },

        // ********
        // * Code *
        // ********
        code: (props) => {
          const { children, className, node, ...rest } = props;
          const match = /language-(\w+)/.exec(className || '');

          return (
            <>
              <GlobalStyles
                styles={{
                  code: { color: 'inherit', background: 'transparent' },
                }}
              />
              <SyntaxHighlighter
                {...(rest as any)}
                wrapLongLines
                style={
                  theme.palette.mode === 'light'
                    ? lightHighlightStyle
                    : darkHighlightStyle
                }
                language={match ? match[1] : 'text'}
                PreTag="span"
                children={String(children).replace(/\n$/, '')}
                customStyle={{ display: 'inline', padding: 0 }}
              />
            </>
          );
        },

        ...componentsProp,
      };
    }, [componentsProp, theme.palette.mode]);

    return (
      <Box
        sx={{
          '> *:first-child': {
            mt: 0,
          },
          '> blockquote>:first-child': {
            mt: 0,
          },
        }}
      >
        <ReactMarkdown
          remarkPlugins={[
            remarkBreaks,
            remarkDirective,
            remarkDirectiveRehype,
            remarkGfm,
          ]}
          components={components}
        >
          {markdown}
        </ReactMarkdown>
      </Box>
    );
  },
);
